import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { BizyState } from '../../bizy';

const STORAGE_KEY = 'bizy-admin-whats-new-tip';

export interface WhatsNewTip {
	id: string;
	headlineKey: string;
	promptKey: string;
	state: BizyState;
	/** Internal note; tips with a comment are excluded from the daily rotation. */
	comment?: string;
}

/** Tips eligible for the admin empty-state daily rotation (no comment set). */
export function isTipInRotation(tip: WhatsNewTip): boolean {
	return !tip.comment?.trim();
}

function isTipIndexInRotation(index: number): boolean {
	const tip = WHATS_NEW_TIPS[index];
	return tip !== undefined && isTipInRotation(tip);
}

export function getFirstRotatingTipIndex(): number {
	const index = WHATS_NEW_TIPS.findIndex(isTipInRotation);
	return index === -1 ? 0 : index;
}

export const WHATS_NEW_TIPS: WhatsNewTip[] = [
	{
		id: 'program-health',
		headlineKey: 'whatsNew.tips.programHealth.headline',
		promptKey: 'whatsNew.tips.programHealth.prompt',
		state: 'excited',
	},
	{
		id: 'struggling',
		headlineKey: 'whatsNew.tips.struggling.headline',
		promptKey: 'whatsNew.tips.struggling.prompt',
		state: 'excited',
		comment: 'Link in prompt is not working — excluded from rotation until fixed.',
	},
	{
		id: 'culture-champions',
		headlineKey: 'whatsNew.tips.cultureChampions.headline',
		promptKey: 'whatsNew.tips.cultureChampions.prompt',
		state: 'excited',
	},
	{
		id: 'combine-data',
		headlineKey: 'whatsNew.tips.combineData.headline',
		promptKey: 'whatsNew.tips.combineData.prompt',
		state: 'excited',
	},
	{
		id: 'export-data',
		headlineKey: 'whatsNew.tips.exportData.headline',
		promptKey: 'whatsNew.tips.exportData.prompt',
		state: 'excited',
		comment:
			'For admins the link is correct but Bizy answers with activity for the current user only, not company redemptions. Confirm expected behavior for admin vs non-admin before re-enabling.',
	},
	{
		id: 'recognition-gaps',
		headlineKey: 'whatsNew.tips.recognitionGaps.headline',
		promptKey: 'whatsNew.tips.recognitionGaps.prompt',
		state: 'excited',
		comment:
			'Bizy assumes manager-to-direct-report recognition and asks for more info instead of answering. Should answer company-wide and optionally ask a follow-up. Confirm behavior before re-enabling.',
	},
	{
		id: 'strongest-bonds',
		headlineKey: 'whatsNew.tips.strongestBonds.headline',
		promptKey: 'whatsNew.tips.strongestBonds.prompt',
		state: 'excited',
	},
	{
		id: 'super-connectors',
		headlineKey: 'whatsNew.tips.superConnectors.headline',
		promptKey: 'whatsNew.tips.superConnectors.prompt',
		state: 'excited',
	},
	{
		id: 'new-hires',
		headlineKey: 'whatsNew.tips.newHires.headline',
		promptKey: 'whatsNew.tips.newHires.prompt',
		state: 'excited',
	},
];

interface StoredTipState {
	lastShownDate: string;
	lastTipIndex: number;
	dismissed: boolean;
}

export function getDateKey(): string {
	return new Date().toISOString().split('T')[0];
}

function getStoredState(): StoredTipState | null {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return null;
		return JSON.parse(stored) as StoredTipState;
	} catch {
		return null;
	}
}

function setStoredState(state: StoredTipState): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch {
		// localStorage unavailable
	}
}

export function getNextTipIndex(currentIndex: number): number {
	const tipCount = WHATS_NEW_TIPS.length;
	for (let offset = 1; offset <= tipCount; offset++) {
		const nextIndex = (currentIndex + offset) % tipCount;
		if (isTipIndexInRotation(nextIndex)) {
			return nextIndex;
		}
	}
	return currentIndex;
}

interface UseAdminWhatsNewTipOptions {
	isAdmin: boolean;
	isEmptyState: boolean;
	isPanelOpen: boolean;
}

interface UseAdminWhatsNewTipResult {
	tip: WhatsNewTip | null;
	dismiss: () => void;
	shouldShowTip: boolean;
}

/**
 * Picks which "What's new" tip to show admins in the empty Bizy panel.
 *
 * One tip per calendar day (UTC), stored in localStorage per browser — not per user account.
 * Each new day advances to the next tip in WHATS_NEW_TIPS (skipping entries with a `comment`).
 */
export function useAdminWhatsNewTip({ isAdmin, isEmptyState, isPanelOpen }: UseAdminWhatsNewTipOptions): UseAdminWhatsNewTipResult {
	const [isDismissedToday, setIsDismissedToday] = useState(false);
	const hasPersistedRef = useRef(false);

	// Resolve today's tip from localStorage, or pick the next tip when the date rolls over.
	const { tip, tipIndex, needsPersist } = useMemo(() => {
		if (!isAdmin) return { tip: null, tipIndex: -1, needsPersist: false };

		const today = getDateKey();
		const stored = getStoredState();

		if (stored && stored.lastShownDate === today) {
			// Already assigned a tip for today — reuse it unless the user dismissed it.
			if (stored.dismissed) {
				return { tip: null, tipIndex: stored.lastTipIndex, needsPersist: false };
			}
			const storedTip = WHATS_NEW_TIPS[stored.lastTipIndex];
			if (storedTip && isTipInRotation(storedTip)) {
				return { tip: storedTip, tipIndex: stored.lastTipIndex, needsPersist: false };
			}
			// Stored index points at a tip removed from rotation (e.g. has `comment`) — hide for today.
			return { tip: null, tipIndex: stored.lastTipIndex, needsPersist: false };
		}

		// New day (or first visit): advance rotation from the last shown index, or start at the first rotating tip.
		const nextIndex = stored ? getNextTipIndex(stored.lastTipIndex) : getFirstRotatingTipIndex();
		const nextTip = WHATS_NEW_TIPS[nextIndex];
		if (!nextTip || !isTipInRotation(nextTip)) {
			return { tip: null, tipIndex: nextIndex, needsPersist: false };
		}
		// needsPersist: write today's date + index so revisits the same day show this tip again.
		return { tip: nextTip, tipIndex: nextIndex, needsPersist: true };
	}, [isAdmin]);

	// Persist new tip state outside of useMemo (side effect must be in useEffect)
	useEffect(() => {
		if (needsPersist && !hasPersistedRef.current) {
			hasPersistedRef.current = true;
			setStoredState({ lastShownDate: getDateKey(), lastTipIndex: tipIndex, dismissed: false });
		}
	}, [needsPersist, tipIndex]);

	useEffect(() => {
		const stored = getStoredState();
		if (stored && stored.lastShownDate === getDateKey()) {
			setIsDismissedToday(stored.dismissed);
		}
	}, []);

	// Hide the tip for the rest of today. Tomorrow still advances to the next tip in rotation.
	// Also triggered when the user clicks the prompt or starts typing in the chat input.
	const dismiss = useCallback(() => {
		const today = getDateKey();
		const stored = getStoredState();
		setStoredState({
			lastShownDate: today,
			lastTipIndex: stored?.lastTipIndex ?? tipIndex,
			dismissed: true,
		});
		setIsDismissedToday(true);
	}, [tipIndex]);

	const shouldShowTip = isAdmin && isEmptyState && isPanelOpen && tip !== null && !isDismissedToday;

	return {
		tip: shouldShowTip ? tip : null,
		dismiss,
		shouldShowTip,
	};
}
